// Executive Summary — dark Vasana dashboard ported from the Claude Design source (V2),
// driven by LIVE data from the Flask backend. The assessment switchboard (now inside
// the Filters slide-out), Organizational Readiness Index, and scorecard are the
// design's interactive model; the population, Vasana Type mix, demographics,
// completion, org filter, and per-assessment scores are live.
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchOrganizations, fetchSummary, fetchFilterOptions, setFilter, fetchMe } from "../../redux/actions";
import {
  ASSESSMENTS, scoreBandFor, computeIndex,
  SWITCHBOARD_GROUPS, READINESS_BANDS, readinessBand, vasanaSignal,
} from "../../design/data";
import { ScoreCard, Drawer, ConfigPanel, InfoTip } from "../../design/components";
import { Gauge, Donut, AgeBars } from "../../design/charts";
import { DD, DD_TYPE_INDEX, DD_INDEX_KEYS } from "../../design/dd-data";
import { DeepDive } from "../../design/dd-deepdive";
import "../../design/styles.css";

const DIST_TO_TYPE = { Sa: "SA", Re: "RE", Ga: "GA", Ma: "MA" };

// Which deep-dive band a 0–100 score falls into. Bands are contiguous [lo, hi);
// the top band is inclusive of 100.
function ddBandLabel(score, bands) {
  if (score == null || !bands) return null;
  const b = bands.find((x) => (score >= x.lo && score < x.hi) || (x.hi >= 100 && score >= x.lo));
  return b ? b.label : bands[bands.length - 1].label;
}

// Round a set of counts to integer percentages that sum to EXACTLY 100
// (largest-remainder / Hamilton method). Rounding each share independently can
// drift to 99 or 101; this floors every share then distributes the leftover
// point(s) to the largest fractional remainders.
function toPercentages(values) {
  const total = values.reduce((s, v) => s + v, 0);
  if (total <= 0) return values.map(() => 0);
  const raw = values.map((v) => (v / total) * 100);
  const out = raw.map((r) => Math.floor(r));
  let leftover = 100 - out.reduce((s, v) => s + v, 0);
  const byFrac = raw
    .map((r, i) => ({ i, frac: r - Math.floor(r) }))
    .sort((a, b) => b.frac - a.frac);
  for (let k = 0; k < leftover; k++) out[byFrac[k % byFrac.length].i] += 1;
  return out;
}

// Overlay the live Vasana cross-assessment matrix onto the design DD_TYPE_INDEX.
// Cells with no respondents in scope (null) keep their design value.
function mergeTypeIndex(live) {
  if (!live) return DD_TYPE_INDEX;
  const out = {};
  Object.keys(DD_TYPE_INDEX).forEach((t) => {
    out[t] = { ...DD_TYPE_INDEX[t] };
    const lt = live[t];
    if (lt) DD_INDEX_KEYS.forEach((k) => {
      if (lt[k] != null) out[t][k] = lt[k];
    });
  });
  return out;
}

// Merge live deep-dive data (keyed by assessment_id) onto the design DD model.
// Live: headline score, band, trend, respondents n, participation, band
// distribution, wave trend, and the Vasana mix. Static (not stored in the DB):
// dimension/subscale bars, weights, patterns, subscale profiles, safety shares,
// cross-assessment matrix — those keep their design values.
function useLiveDeepDive(deepDive, distribution, population, typeIndex) {
  return useMemo(() => {
    const liveById = {};
    (deepDive || []).forEach((d) => {
      liveById[d.assessment_id] = d;
    });

    const merged = { order: DD.order, data: {}, typeIndex: mergeTypeIndex(typeIndex) };
    Object.entries(DD.data).forEach(([id, meta]) => {
      if (meta.categorical) {
        // Vasana Type tile: takers = members who completed the assessment.
        const takers = (distribution || []).reduce((s, x) => s + (x.value || 0), 0);
        let { mix, dominant } = meta;
        if (takers > 0) {
          const pcts = toPercentages(distribution.map((x) => x.value || 0));
          mix = distribution
            .map((x, i) => ({ type: DIST_TO_TYPE[x.key] || x.key.toUpperCase(), pct: pcts[i] }))
            .sort((p, q) => q.pct - p.pct);
          dominant = mix[0].type;
        }
        // Respondents (n) = members in scope (= Section 1 "population in scope").
        // Participation = % of those members who have taken the assessment.
        const n = population != null ? population : meta.n;
        const part = population ? Math.round((takers / population) * 100) : meta.part;
        merged.data[id] = { ...meta, mix, dominant, n, part };
        return;
      }

      const live = liveById[meta.assessmentId];
      if (!live || live.score == null) {
        merged.data[id] = { ...meta };
        return;
      }

      const score = live.score;
      let trend = meta.trend;
      if (live.prior != null) {
        const d = score - live.prior;
        trend = {
          dir: d > 0 ? "up" : d < 0 ? "down" : "flat",
          value: Math.abs(d),
          improving: d === 0 ? null : meta.higherWorse ? d < 0 : d > 0,
          note: meta.trend?.note,
        };
      }

      // Wellness Indicator: live per-domain scores (from question selections)
      // replace the design dims AND the weighted-contribution scores, matched by
      // label; any label the backend can't score keeps its design value.
      let dims = meta.dims;
      let weights = meta.weights;
      let subscales = meta.subscales;
      if (live.dims && live.dims.length) {
        const liveByLabel = {};
        live.dims.forEach((d) => {
          if (d.score != null) liveByLabel[d.label] = d.score;
        });
        if (meta.dims) dims = meta.dims.map((d) => (liveByLabel[d.label] != null ? { ...d, score: liveByLabel[d.label] } : d));
        if (meta.weights) weights = meta.weights.map((w) => (liveByLabel[w.label] != null ? { ...w, score: liveByLabel[w.label] } : w));
        // ProQOL three-subscale profile draws its scores from the same dimension values.
        if (meta.subscales) subscales = meta.subscales.map((s) => (liveByLabel[s.label] != null ? { ...s, score: liveByLabel[s.label] } : s));
      }

      // Order the band distribution to follow the design `distBands` (authored
      // bad → good), so every assessment's chart reads worst-first regardless of
      // the order the backend emits its buckets in.
      const rawDist = live.dist && live.dist.length ? live.dist : meta.dist;
      const bandOrder = (meta.distBands || meta.bands || []).map((b) => b.label);
      const dist = rawDist && bandOrder.length
        ? [...rawDist].sort(
            (x, y) =>
              (bandOrder.indexOf(x.label) + 1 || 99) - (bandOrder.indexOf(y.label) + 1 || 99)
          )
        : rawDist;

      merged.data[id] = {
        ...meta,
        score,
        band: ddBandLabel(score, meta.bands) || meta.band,
        trend,
        n: live.responses != null ? live.responses : meta.n,
        part: live.participation != null ? live.participation : meta.part,
        dist,
        dims,
        weights,
        subscales,
        // journey assessments keep waves:null so the component uses the journey trail
        waves: meta.module === "journey" ? meta.waves : live.waves && live.waves.length ? live.waves : meta.waves,
      };
    });
    return merged;
  }, [deepDive, distribution, population, typeIndex]);
}

// Merge static assessment metadata with LIVE backend data:
//  - Vasana Type: mix/dominant from the marker distribution.
//  - Scored assessments: eval/prior/responses -> score, aligned (index),
//    band/tone/color (ported ranges), and trend (latest vs prior).
function useLiveAssessments(distribution, assessmentScores) {
  return useMemo(() => {
    const scoreById = {};
    (assessmentScores || []).forEach((s) => {
      scoreById[s.assessment_id] = s;
    });

    return ASSESSMENTS.map((meta) => {
      if (meta.categorical) {
        const total = (distribution || []).reduce((s, d) => s + (d.value || 0), 0);
        let mix = [];
        let dominant = null;
        if (total > 0) {
          const pcts = toPercentages(distribution.map((d) => d.value || 0));
          mix = distribution
            .map((d, i) => ({ type: DIST_TO_TYPE[d.key] || d.key.toUpperCase(), pct: pcts[i] }))
            .sort((x, y) => y.pct - x.pct);
          dominant = mix[0].type;
        }
        // "What this signals" is written around whichever type is dominant.
        return { ...meta, mix, dominant, responses: total, desc: vasanaSignal(dominant) };
      }

      const live = scoreById[meta.assessmentId];
      const evalScore = live && live.eval != null ? live.eval : null;
      const prior = live && live.prior != null ? live.prior : null;
      const responses = live ? live.responses : 0;
      const aligned = evalScore == null ? null : meta.invert ? 100 - evalScore : evalScore;
      const priorAligned = prior == null ? null : meta.invert ? 100 - prior : prior;
      // Band label + colour come from the same set the drawer's Score Legend shows,
      // so the score, the chip, and the highlighted legend row always agree.
      const b = scoreBandFor({ ...meta, score: evalScore });

      let trend = null;
      if (aligned != null && priorAligned != null) {
        const d = aligned - priorAligned;
        trend = {
          dir: d > 0 ? "up" : d < 0 ? "down" : "flat",
          value: Math.abs(Math.round(d)),
          improving: d === 0 ? null : d > 0,
        };
      }

      return {
        ...meta,
        score: evalScore,
        aligned,
        prior,
        priorAligned,
        responses,
        band: b ? b.label : "—",
        color: b ? b.color : null,
        trend,
      };
    });
  }, [distribution, assessmentScores]);
}

function initialActive(assessments) {
  const base = {};
  assessments.forEach((a) => {
    base[a.id] = true;
  });
  return base;
}

export default function ExecutiveSummary() {
  const dispatch = useDispatch();

  const filters = useSelector((s) => s.ui.filters);
  const options = useSelector((s) => s.ui.options);
  const { list: organizations } = useSelector((s) => s.organizations);
  const me = useSelector((s) => s.me);
  // Organization admins are scoped to a single org: the backend clamps every
  // response to it, and here we hide the Organization filter and lock the scope.
  const isOrgAdmin = me.role === "org_admin";
  const { loading, error, distribution, demographics, assessmentScores, deepDive, typeIndex, required } = useSelector((s) => s.summary);

  const assessments = useLiveAssessments(distribution, assessmentScores);
  const deepDiveData = useLiveDeepDive(deepDive, distribution, demographics?.population, typeIndex);

  const [active, setActive] = useState(() => initialActive(ASSESSMENTS));
  const [drawerA, setDrawerA] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchMe());
    dispatch(fetchOrganizations());
  }, [dispatch]);

  // Once we know the caller is an org admin, lock the scope to their own org so the
  // Location → Department → Event cascade (and its option fetch) work. The backend
  // enforces the scope regardless; this just aligns the UI. Only fires when the
  // locked org isn't already selected, so it never fights the user.
  useEffect(() => {
    if (
      isOrgAdmin &&
      me.organization_id != null &&
      String(filters.org_id) !== String(me.organization_id)
    ) {
      dispatch(setFilter({ org_id: String(me.organization_id) }));
    }
  }, [dispatch, isOrgAdmin, me.organization_id, filters.org_id]);

  useEffect(() => {
    dispatch(fetchSummary(filters));
  }, [dispatch, filters]);

  // Refresh the cascading location/department/event options when the org changes.
  useEffect(() => {
    dispatch(fetchFilterOptions(filters.org_id));
  }, [dispatch, filters.org_id]);

  const index = computeIndex(assessments, active);
  const rband = index ? readinessBand(index.value) : null;
  const activeList = assessments.filter((a) => active[a.id]);
  const population = demographics?.population;
  const orgName = isOrgAdmin
    ? me.organization_name ||
      organizations.find((o) => String(o.id) === String(me.organization_id))?.name ||
      "Organization"
    : filters.org_id
    ? organizations.find((o) => String(o.id) === String(filters.org_id))?.name || "Organization"
    : "All organizations";

  function toggle(id) {
    setActive((p) => ({ ...p, [id]: !p[id] }));
  }
  function selectAll() {
    const n = {};
    assessments.forEach((a) => (n[a.id] = true));
    setActive(n);
  }
  function clearAll() {
    const n = {};
    assessments.forEach((a) => (n[a.id] = false));
    setActive(n);
  }

  // Live tile values. The required-assessment tiles come straight from the backend
  // (a member's required set is defined in the DB), so they do NOT react to the
  // assessment switchboard toggles.
  const fmt = (n) => (typeof n === "number" ? n.toLocaleString("en-US") : "—");

  return (
    <div className="es-page">
      <main className="es-wrap">
        {/* HEADER */}
        <header className="es-header">
          <div>
            <h1 className="es-header__title">Intelligence Dashboard</h1>
            <div className="es-header__org">
              <i className="ph-fill ph-shield-star"></i>
              {orgName}
            </div>
          </div>
          <div className="es-header__actions">
            <button className="es-header__period" onClick={() => setPanelOpen(true)}>
              <i className="ph ph-calendar-blank"></i>Reporting period · Q2 2026
            </button>
            <button className="es-configbtn" onClick={() => setPanelOpen(true)} aria-label="Filters">
              <i className="ph ph-sliders-horizontal"></i>
              <span>Filters</span>
            </button>
          </div>
        </header>

        {/* Global error banner */}
        {error && (
          <section className="es-index__note" style={{ borderRadius: "var(--vas-r-lg)" }}>
            <i className="ph ph-warning-circle"></i>
            <div>
              <p className="es-eyebrow" style={{ marginBottom: 6 }}>Couldn't load live data</p>
              <p>{error}</p>
              <button className="es-btn" style={{ marginTop: 12 }} onClick={() => dispatch(fetchSummary(filters))}>
                <i className="ph ph-arrows-clockwise"></i>Retry
              </button>
            </div>
          </section>
        )}

        {/* ORGANIZATIONAL READINESS INDEX */}
        {index ? (
          <section className="es-index">
            <div className="es-index__gaugewrap">
              <Gauge value={index.value} color={rband.color} />
              <div className="es-gauge__center">
                <div className="es-gauge__val" key={index.value} style={{ color: rband.color }}>
                  {index.value}
                  <small>/100</small>
                </div>
                <div className="es-gauge__band" style={{ color: rband.color }}>{rband.label}</div>
              </div>
            </div>
            <div>
              <p className="es-eyebrow">
                Organizational Readiness Index
                <InfoTip text="This score is the average across your currently active scored assessments. Toggling assessments in Filters recomposes it. Burnout is inverted so higher always means healthier; Vasana Type is excluded as a disposition profile." />
              </p>
              <h2 className="es-index__title">
                The organization is in the <span style={{ color: rband.color }}>{rband.label}</span> band
              </h2>
              <p className="es-index__caption">
                This index aggregates scores across the {index.count} active scored assessment
                {index.count > 1 ? "s" : ""} into a single organizational readiness score, calibrated so a higher
                score reflects a healthier, more resilient population.
              </p>
            </div>
            <div className="es-legend">
              {READINESS_BANDS.map((b) => (
                <div className={"es-legend__item" + (b.label === rband.label ? " is-active" : "")} key={b.label}>
                  <span className="es-legend__dot" style={{ background: b.color }}></span>
                  <span className="es-legend__range">{b.min}–{b.max}</span>
                  <span className="es-legend__lbl">{b.label}</span>
                </div>
              ))}
            </div>
          </section>
        ) : (
          <section className="es-index" style={{ gridTemplateColumns: "1fr" }}>
            <div className="es-index__note">
              <i className="ph ph-info"></i>
              <div>
                <p className="es-eyebrow" style={{ marginBottom: 6 }}>Organizational Readiness Index</p>
                <p>
                  The index needs at least one scored assessment to calculate. Vasana Type is a disposition profile,
                  not a 0–100 score, so it cannot produce an index on its own. Turn on any scored assessment in
                  Filters to bring the index back.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* KEY METRICS (live) */}
        <section className="es-metrics">
          <div className="es-tile">
            <span className="es-tile__icon"><i className="ph ph-users-three"></i></span>
            <span className="es-tile__val">{fmt(population)}</span>
            <span className="es-tile__lbl">Population in scope</span>
          </div>
          <div className="es-tile">
            <span className="es-tile__icon"><i className="ph ph-check-circle"></i></span>
            <span className="es-tile__val">{fmt(required?.members_completed_all)}</span>
            <span className="es-tile__lbl">Members who completed all required assessments</span>
          </div>
          <div className="es-tile">
            <span className="es-tile__icon"><i className="ph ph-list-checks"></i></span>
            <span className="es-tile__val">
              {typeof required?.completion_pct === "number" ? required.completion_pct : "—"}
              <small>%</small>
            </span>
            <span className="es-tile__lbl">Required assessments completed</span>
          </div>
        </section>

        {/* DEMOGRAPHICS (live) */}
        <section className="es-demo">
          <div className="es-card">
            <h2 className="es-card__title">Age Distribution</h2>
            {loading && !demographics.age.length ? (
              <div className="es-state"><span className="es-spinner" /><span className="es-state__msg">Loading…</span></div>
            ) : (
              <AgeBars data={demographics.age} />
            )}
          </div>
          <div className="es-card">
            <h2 className="es-card__title">Gender Distribution</h2>
            {loading && !demographics.gender.length ? (
              <div className="es-state"><span className="es-spinner" /><span className="es-state__msg">Loading…</span></div>
            ) : (
              <Donut data={demographics.gender} total={population} />
            )}
          </div>
        </section>

        {/* SCORECARD (grouped Behavioral / Clinical) */}
        <section>
          <div className="es-card__head" style={{ padding: "0 4px" }}>
            <div>
              <h2 className="es-card__title" style={{ fontSize: "var(--vas-fs-h2)" }}>Assessment scorecard</h2>
              <p className="es-card__sub">Click any card to explore the detail behind the score.</p>
            </div>
          </div>
          {activeList.length === 0 ? (
            <div className="es-grid">
              <div className="es-empty">
                <i className="ph ph-cards"></i>No assessments active. Open Filters to turn some on.
              </div>
            </div>
          ) : (
            SWITCHBOARD_GROUPS.map((g) => {
              const cards = g.ids
                .map((id) => assessments.find((a) => a.id === id))
                .filter((a) => a && active[a.id]);
              if (!cards.length) return null;
              return (
                <div className="es-scoregroup" key={g.label}>
                  <div className="es-scoregroup__label">{g.label}</div>
                  <div className="es-grid">
                    {cards.map((a) => (
                      <ScoreCard key={a.id} a={a} onOpen={setDrawerA} />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </section>
      </main>
      <DeepDive data={deepDiveData} />
      <ConfigPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        active={active}
        toggle={toggle}
        selectAll={selectAll}
        clearAll={clearAll}
        organizations={organizations}
        filters={filters}
        options={options}
        onChange={(patch) => dispatch(setFilter(patch))}
        lockOrg={isOrgAdmin}
        orgName={orgName}
      />
      <Drawer a={drawerA} population={population} onClose={() => setDrawerA(null)} />
    </div>
  );
}
